import * as React from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { Document } from '../types';
// import mockDocuments from '../data/mock-documents'; // Will be replaced by API call
import { DocumentsService } from '../services/documents-service';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import { 
  Calendar, 
  Download, 
  Edit, 
  Eye, 
  FileText, 
  Globe, 
  Info, 
  Languages, 
  Link2, 
  Trash2, 
  User 
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { DocumentPreviewDialog } from '../components/DocumentPreviewDialog';

const documentTypeLabels: Record<string, string> = {
  article: 'Article',
  advertisement: 'Advertisement',
  editorial: 'Editorial',
  photo: 'Photograph',
  letter: 'Letter',
  document: 'Document',
  other: 'Other',
};

const visibilityLabels: Record<string, string> = {
  public: 'Public',
  private: 'Private',
  restricted: 'Restricted',
};

const conditionLabels: Record<string, string> = {
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
};

export function DocumentDetailsPage() {
  const { id } = useParams({ from: '/_authenticated/documents/$id' });
  const [document, setDocument] = React.useState<Document | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  React.useEffect(() => {
    const fetchDocument = async () => {
      setIsLoading(true);
      try {
        const fetchedDocument = await DocumentsService.getDocumentById(id);
        setDocument(fetchedDocument);
      } catch (err: any) {
        console.error('Failed to fetch document:', err);
        toast({
          title: 'Error',
          description: err.message || 'Failed to load document. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, [id]); // Remove toast from dependencies to prevent infinite re-renders

  const handleDownload = async () => {
    if (!document) return;
    
    try {
      // Create a download URL based on the document's storage path
      const downloadUrl = `/api/documents/${document.id}/download`;
      
      // Create a temporary link and trigger download
      const link = window.document.createElement('a');
      link.href = downloadUrl;
      link.download = `${document.title}.${document.media.file_type.split('/')[1] || 'pdf'}`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      
      toast({
        title: 'Download Started',
        description: `Downloading ${document.title}`,
      });
    } catch (error) {
      console.error('Download failed:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to download the document. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!document) return;
    
    try {
      // Instead of deleting, set visibility to private (soft delete)
      await DocumentsService.updateDocument(document.id, {
        visibility: 'private'
      });
      
      toast({
        title: 'Document Archived',
        description: 'Document has been moved to private (archived)',
      });
      
      // Navigate back to documents list
      navigate({ to: '/documents' });
    } catch (error) {
      console.error('Failed to archive document:', error);
      toast({
        title: 'Archive Failed',
        description: 'Failed to archive document. Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading document...</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <Alert variant="destructive">
        <Info className="h-4 w-4" />
        <AlertTitle>Document Not Found</AlertTitle>
        <AlertDescription>
          The document with ID {id} could not be found. It may have been deleted or you may not have permission to view it.
        </AlertDescription>
      </Alert>
    );
  }

  const createdDate = new Date(document.audit.created_at * 1000);
  const updatedDate = new Date(document.audit.updated_at * 1000);
  const publishedDate = document.audit.published_at 
    ? new Date(document.audit.published_at * 1000) 
    : null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <PageHeader
          title={document.title}
          description={document.description || 'No description provided'}
        />
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleDownload}>
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
          <Button variant="outline" size="sm" onClick={() => setIsPreviewOpen(true)}>
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </Button>
          <Button size="sm" onClick={() => window.location.href = `/documents/edit/${document.id}`}>
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDelete}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge variant="outline" className="bg-muted">
          {documentTypeLabels[document.document_type]}
        </Badge>
        <Badge 
          variant="outline" 
          className={
            document.visibility === 'public' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
              : document.visibility === 'private'
                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
          }
        >
          {visibilityLabels[document.visibility]}
        </Badge>
        {document.language && (
          <Badge variant="outline">
            <Languages className="mr-1 h-3 w-3" />
            {document.language.toUpperCase()}
          </Badge>
        )}
        {document.original_date && (
          <Badge variant="outline">
            <Calendar className="mr-1 h-3 w-3" />
            {document.original_date}
          </Badge>
        )}
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="metadata">Metadata</TabsTrigger>
          <TabsTrigger value="preservation">Preservation</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Document Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {document.creator && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Creator</span>
                    <span className="text-sm text-muted-foreground">{document.creator}</span>
                  </div>
                )}
                {document.location && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Location</span>
                    <span className="text-sm text-muted-foreground">{document.location}</span>
                  </div>
                )}
                {document.source && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Source</span>
                    <span className="text-sm text-muted-foreground">{document.source}</span>
                  </div>
                )}
                {document.archive_source_location && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Archive Location</span>
                    <span className="text-sm text-muted-foreground">{document.archive_source_location}</span>
                  </div>
                )}
                {document.publication_name && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Publication</span>
                    <span className="text-sm text-muted-foreground">{document.publication_name}</span>
                  </div>
                )}
                {document.page_number && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Page Number</span>
                    <span className="text-sm text-muted-foreground">{document.page_number}</span>
                  </div>
                )}
                {document.section && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Section</span>
                    <span className="text-sm text-muted-foreground">{document.section}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Media Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm font-medium">File Type</span>
                  <span className="text-sm text-muted-foreground">{document.media.file_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">File Size</span>
                  <span className="text-sm text-muted-foreground">
                    {(document.media.file_size / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
                {document.media.image_resolution && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Resolution</span>
                    <span className="text-sm text-muted-foreground">{document.media.image_resolution}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Color Mode</span>
                  <span className="text-sm text-muted-foreground">
                    {document.media.color_mode.replace(/_/g, ' ')}
                  </span>
                </div>
                {document.media.ocr_confidence_score !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">OCR Confidence</span>
                    <span className="text-sm text-muted-foreground">
                      {Math.round(document.media.ocr_confidence_score * 100)}%
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm font-medium">OCR Quality</span>
                  <span className="text-sm text-muted-foreground">
                    {document.ocr_quality.charAt(0).toUpperCase() + document.ocr_quality.slice(1)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {document.historical_context && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Historical Context</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{document.historical_context}</p>
              </CardContent>
            </Card>
          )}

          {document.ocr_text && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">OCR Text</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-64 overflow-y-auto bg-muted p-4 rounded-md">
                  <pre className="text-sm whitespace-pre-wrap">{document.ocr_text}</pre>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="metadata" className="space-y-4 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tags</CardTitle>
              </CardHeader>
              <CardContent>
                {document.tags && document.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {document.tags.map((tag) => (
                      <Badge key={tag} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No tags</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Categories</CardTitle>
              </CardHeader>
              <CardContent>
                {document.categories && document.categories.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {document.categories.map((category) => (
                      <Badge key={category} variant="secondary">
                        {category}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No categories</p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Entities</CardTitle>
            </CardHeader>
            <CardContent>
              {document.entities && document.entities.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {document.entities.map((entity) => (
                    <Badge key={entity} variant="secondary">
                      {entity}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No entities</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Related Items</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {document.relations?.related_articles && document.relations.related_articles.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Related Articles</h4>
                  <div className="flex flex-wrap gap-2">
                    {document.relations.related_articles.map((articleId) => (
                      <Badge key={articleId} variant="outline">
                        <Link2 className="mr-1 h-3 w-3" />
                        {articleId}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {document.relations?.person_id && document.relations.person_id.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Related People</h4>
                  <div className="flex flex-wrap gap-2">
                    {document.relations.person_id.map((personId) => (
                      <Badge key={personId} variant="outline">
                        <User className="mr-1 h-3 w-3" />
                        {personId}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {document.relations?.collections && document.relations.collections.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Collections</h4>
                  <div className="flex flex-wrap gap-2">
                    {document.relations.collections.map((collection) => (
                      <Badge key={collection} variant="outline">
                        <FileText className="mr-1 h-3 w-3" />
                        {collection}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {document.relations?.event_id && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Event ID</span>
                  <span className="text-sm text-muted-foreground">{document.relations.event_id}</span>
                </div>
              )}

              {document.relations?.advertiser_id && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Advertiser ID</span>
                  <span className="text-sm text-muted-foreground">{document.relations.advertiser_id}</span>
                </div>
              )}

              {(!document.relations || 
                (!document.relations.related_articles?.length && 
                 !document.relations.person_id?.length && 
                 !document.relations.collections?.length && 
                 !document.relations.event_id && 
                 !document.relations.advertiser_id)) && (
                <p className="text-sm text-muted-foreground">No related items</p>
              )}
            </CardContent>
          </Card>

          {document.metadata && Object.keys(document.metadata).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Additional Metadata</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(document.metadata).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-sm font-medium">{key}</span>
                      <span className="text-sm text-muted-foreground">{value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="preservation" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Preservation Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Source Archive</span>
                <span className="text-sm text-muted-foreground">{document.preservation.source_archive}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Date Archived</span>
                <span className="text-sm text-muted-foreground">
                  {format(new Date(document.preservation.date_archived), 'PPP')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Condition</span>
                <span className="text-sm text-muted-foreground">
                  {conditionLabels[document.preservation.condition]}
                </span>
              </div>
              {document.preservation.handling_restrictions && (
                <div>
                  <span className="text-sm font-medium">Handling Restrictions</span>
                  <p className="text-sm text-muted-foreground mt-1">{document.preservation.handling_restrictions}</p>
                </div>
              )}
              {document.preservation.archivist_notes && (
                <div>
                  <span className="text-sm font-medium">Archivist Notes</span>
                  <p className="text-sm text-muted-foreground mt-1">{document.preservation.archivist_notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Document History</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="mr-4 mt-0.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
                      <FileText className="h-4 w-4 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Document Created</p>
                    <p className="text-sm text-muted-foreground">
                      Created by User ID: {document.audit.created_by}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(createdDate, 'PPpp')}
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="flex items-start">
                  <div className="mr-4 mt-0.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                      <Edit className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Last Updated</p>
                    <p className="text-xs text-muted-foreground">
                      {format(updatedDate, 'PPpp')}
                    </p>
                  </div>
                </div>

                {publishedDate && (
                  <>
                    <Separator />
                    <div className="flex items-start">
                      <div className="mr-4 mt-0.5">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                          <Globe className="h-4 w-4 text-green-600 dark:text-green-300" />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">Published</p>
                        <p className="text-xs text-muted-foreground">
                          {format(publishedDate, 'PPpp')}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Document Preview Dialog */}
      <DocumentPreviewDialog
        document={document}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </div>
  );
}
